import { Typography } from 'src/components/new/Typography';
import { Tooltip } from '@mui/material';
import { EditAuditLogEntry, DocumentSnapshot, AuditLogState, formatPacificDate } from './ChainOfCustody';

interface AuditLogEntryProps {
  entry: EditAuditLogEntry;
  isCosMember: boolean;
}

const formatInvoice = (amount: number): string => {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const capitalizeService = (service?: string): string => {
  if (!service) return 'Unknown';
  return service.charAt(0).toUpperCase() + service.slice(1);
};

const renderDocumentStateSection = (label: string, state: AuditLogState) => {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <Typography sx={{ fontWeight: '500', marginBottom: '0.25rem', paddingLeft: '1.5rem' }}>
        {label}:
      </Typography>
      <ul style={{ margin: 0, paddingLeft: '2.5rem', listStyleType: 'disc' }}>
        {state.documents.map((doc, index) => (
          <li key={index} style={{ marginBottom: '0.25rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <span style={{ fontWeight: '600' }}>{doc.filename}</span>
            {' '}
            <span style={{ fontStyle: 'italic' }}>
              ("{doc.description}", ECF {doc.ecfReference}, {doc.type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}{doc.paperColor === 'color' ? ', Colored' : doc.paperColor === 'standard' ? ', Standard' : ''})
            </span>
            , {doc.pageCount} pages
          </li>
        ))}
        <li style={{ marginBottom: '0.25rem' }}>
          <span>Invoice: {formatInvoice(state.invoiceTotal)}</span>
        </li>
      </ul>
    </div>
  );
};

const renderPreferencesStateSection = (label: string, state: AuditLogState) => {
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <Typography sx={{ fontWeight: '500', marginBottom: '0.25rem', paddingLeft: '1.5rem' }}>
        {label}:
      </Typography>
      <ul style={{ margin: 0, paddingLeft: '2.5rem', listStyleType: 'disc' }}>
        <li style={{ marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: '500' }}>Service: </span>
          {capitalizeService(state.service)}
        </li>
        <li style={{ marginBottom: '0.25rem' }}>
          <span>Invoice: {formatInvoice(state.invoiceTotal)}</span>
        </li>
      </ul>
    </div>
  );
};

const AuditLogEntry = ({ entry, isCosMember }: AuditLogEntryProps) => {
  const adminName = isCosMember
    ? `${entry.adminFirstName} ${entry.adminLastName}`
    : 'CoS Team';

  const isPreferencesEdit = entry.editType === 'service_type_and_invoice';
  const actionLabel = isPreferencesEdit ? 'edited the preferences' : 'edited the documents';

  return (
    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
      {/* Timeline dot and line */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '0.25rem' }}>
        <div style={{
          width: '1.25rem',
          height: '1.25rem',
          borderRadius: '50%',
          border: '2px solid rgba(67, 110, 178, 0.30)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <path d="M2 6L5 9L10 3" stroke="rgba(67, 110, 178, 0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <div style={{ width: '2px', flexGrow: 1, backgroundColor: 'rgba(67, 110, 178, 0.30)', marginTop: '0.25rem' }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1 }}>
        <Typography sx={{ fontWeight: '500', marginBottom: '0.5rem' }}>
          {isCosMember && entry.adminEmail ? (
            <Tooltip title={entry.adminEmail} arrow placement="top" componentsProps={{ tooltip: { sx: { fontSize: '0.875rem', padding: '0.5rem 0.75rem' } } }}>
              <span style={{ color: '#1976d2', textDecoration: 'underline', cursor: 'pointer' }}>{adminName}</span>
            </Tooltip>
          ) : (
            <span style={{ fontWeight: '600' }}>{adminName}</span>
          )}
          {' '}{actionLabel}{'  '}
          <span style={{ fontWeight: '400', color: '#666' }}>{formatPacificDate(entry.createdAt)}</span>
        </Typography>
        {isPreferencesEdit
          ? (
            <>
              {renderPreferencesStateSection('From', entry.previousState)}
              {renderPreferencesStateSection('To', entry.newState)}
            </>
          )
          : (
            <>
              {renderDocumentStateSection('From', entry.previousState)}
              {renderDocumentStateSection('To', entry.newState)}
            </>
          )
        }
      </div>
    </div>
  );
};

export default AuditLogEntry;
